import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Accepted uploads for a design / layout post
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/ogg']
const MAX_IMAGE_MB = 10
const MAX_VIDEO_MB = 100

// Resolves true when the browser can decode the video (e.g. iPhone HEVC .mov
// files often can't be played in Chrome/Windows even though they upload fine)
const canPlayVideo = file =>
  new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const v = document.createElement('video')
    const done = ok => {
      clearTimeout(timer)
      URL.revokeObjectURL(url)
      resolve(ok)
    }
    const timer = setTimeout(() => done(true), 8000) // don't block on slow devices
    v.preload = 'metadata'
    v.muted = true
    v.onloadedmetadata = () => done(v.videoWidth > 0 || v.duration > 0)
    v.onerror = () => done(false)
    v.src = url
  })

const Opentimelinemodal = ({
  show,
  onClose,
  onImagetimeline,
  onSubmitpost, 
  fetchinputs,
  inputData
}) => {
  const [file, setFile] = useState(null)
  const [text, setText] = useState('')
  const [project, setProject] = useState('')
  const [wing, setWing] = useState('')
  const [filteredWings, setFilteredWings] = useState([])

  const [projectError, setProjectError] = useState('')
  const [wingError, setWingError] = useState('')
  const [fileError, setFileError] = useState('')
  const [textError, setTextError] = useState('')
  const [loading, setLoading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef(null)

  useEffect(() => {
    console.log('Modal inputData updated:', inputData)
  }, [inputData])

  const isVideo = !!file && file.type.startsWith('video/')
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])
  if (!show) return null

  const uniqueProjects = Array.from(
    new Map(
      inputData.map(item => [
        item.projectId,
        { id: item.projectId, name: item.projectName }
      ])
    ).values()
  )


  const handleProjectChange = e => {
    const selectedProjectId = e.target.value
    setProject(selectedProjectId)
    setProjectError('')
    setWing('')

    const wings = inputData.filter(
      item => String(item.projectId) === String(selectedProjectId)
    )

    setFilteredWings(wings)
  }

  const handleWingChange = e => {
    setWing(e.target.value)
    if (e.target.value) setWingError('')
  }


  const validatefile = async e => {
    const selectedFile = e.target.files[0]
    const video = !!selectedFile && VIDEO_TYPES.includes(selectedFile.type)
    const image = !!selectedFile && IMAGE_TYPES.includes(selectedFile.type)
    if (!video && !image) {
      setFile(null)
      setFileError('Please upload a JPEG, PNG or GIF image, or an MP4, WebM or MOV video.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    const maxMb = video ? MAX_VIDEO_MB : MAX_IMAGE_MB
    if (selectedFile.size > maxMb * 1024 * 1024) {
      setFile(null)
      setFileError(`${video ? 'Video' : 'Image'} is too large (max ${maxMb} MB).`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    if (video && !(await canPlayVideo(selectedFile))) {
      setFile(null)
      setFileError('This video format can\'t be played in browsers (common with iPhone HEVC .mov files). Please export it as MP4 (H.264) and try again.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setFile(selectedFile)
    setFileError('')
  }

 
  const validatetext = input => {
    setText(input)
    if (!input || input.trim() === '') {
      setTextError('Text input cannot be empty.')
      return false
    } else {
      setTextError('')
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    let valid = true

    // Project and Wing are optional now; allow null when not provided

    if (!file) {
      setFileError('An image or video is required')
      valid = false
    }

    if (!text.trim()) {
      setTextError('Text is required')
      valid = false
    }

    if (!valid) return

    setLoading(true)
    setUploadProgress(0)
    try {
      const formData = new FormData()
      formData.append('upload', file)
      const uploadRes = await onImagetimeline(formData, setUploadProgress)
      if (!uploadRes) throw new Error('Upload failed')
      const payload = {
        ProjectID: project ? parseInt(project) : null,
        Wing: wing || null,
        Data: {
          Title: text,
          ImageLink: uploadRes,
          // videos share ImageLink; MediaType tells the feed how to render it
          MediaType: isVideo ? 'video' : 'image'
        }
      }

      await onSubmitpost(payload)


      setFile(null)
      setText('')
      setProject('')
      setTextError('')

      setWing('')
      setFilteredWings([])
      if (fileInputRef.current) fileInputRef.current.value = ''

      onClose()
    } catch (err) {
      toast.error('Failed to submit post')
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        scrollable
        backdrop="static"
        aria-labelledby="timeline-modal-title"
      >
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title" id="timeline-modal-title">Add new post to timeline</h5>
                <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
              </div>

              <div className="modal-body">
                {/* <div className="mb-3 text-bold">
                  <label className="form-label">
                    Select Project <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    className="form-control"
                    value={project}
                    onChange={handleProjectChange}
                  >
                    <option value="">Choose Project</option>
                    {uniqueProjects.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {projectError && (
                    <div className="alert alert-danger mt-2">
                      {projectError}
                    </div>
                  )}
                </div> */}

                {/* WING */}
                {/* <div className="mb-3">
                  <div className="mb-3 text-bold">
                  <label className="form-label">
                    Select Wing <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    className="form-control"
                    value={wing}
                    onChange={handleWingChange}
                    disabled={!project}
                  >
                    <option value="">Choose Wing</option>
                    {filteredWings.map(w => (
                      <option key={w.wingId} value={w.wingId}>
                        {w.wingName}
                      </option>
                    ))}
                  </select>
                  {wingError && (
                    <div className="alert alert-danger mt-2">{wingError}</div>
                  )}
                </div>
                </div> */}
                <div className="mb-3">
                  <label className="form-label text-bold" htmlFor="timeline-media">
                    Upload image or video <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    id="timeline-media"
                    type="file"
                    accept="image/png, image/jpeg, image/gif, video/mp4, video/webm, video/quicktime, video/ogg"
                    className="form-control"
                    onChange={validatefile}
                    ref={fileInputRef}
                    aria-describedby="timeline-media-help"
                    disabled={loading}
                  />
                  <div id="timeline-media-help" className="form-text">
                    Images up to {MAX_IMAGE_MB} MB (JPEG, PNG, GIF) · Videos up to {MAX_VIDEO_MB} MB (MP4, WebM, MOV)
                  </div>
                  {fileError && (
                    <div className="alert alert-danger mt-2">{fileError}</div>
                  )}

                  {previewUrl && (
                    <div className="bl-media-preview mt-3">
                      {isVideo ? (
                        <video src={previewUrl} controls muted playsInline preload="metadata" aria-label="Selected video preview" />
                      ) : (
                        <img src={previewUrl} alt="Selected image preview" />
                      )}
                      <div className="bl-media-preview__meta">
                        <span className={`bl-chip ${isVideo ? 'bl-chip--info' : 'bl-chip--primary'}`}>
                          <ion-icon name={isVideo ? 'videocam-outline' : 'image-outline'} aria-hidden="true"></ion-icon>
                          {isVideo ? 'Video' : 'Image'}
                        </span>
                        <span className="bl-media-preview__name">{file.name}</span>
                        <span className="text-muted small">{(file.size / (1024 * 1024)).toFixed(1)} MB</span>
                      </div>
                    </div>
                  )}

                  {loading && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between small mb-1">
                        <span>{uploadProgress < 100 ? 'Uploading…' : 'Saving post…'}</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div
                        className="bl-pay__bar"
                        role="progressbar"
                        aria-valuenow={uploadProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label="Upload progress"
                      >
                        <span style={{ width: `${uploadProgress}%` }}></span>
                      </div>
                    </div>
                  )}
                </div>

                {/* TEXT */}
                <div className="mb-3 text-bold">
                    <label className="form-label font-bold">Enter Text</label>{' '}
                    <span style={{ color: 'red' }}>*</span>
                    <textarea
                      type="text"
                      className="form-control"
                      placeholder="Enter text"
                      maxlength="1000"
                      value={text}
                      onChange={e => validatetext(e.target.value)}
                      required
                    />
                  </div>
                  {textError && (
                    <div className="alert alert-danger mt-2">{textError}</div>
                  )}
                </div>

              <div className="modal-footer">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading && <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>}
                  {loading ? (isVideo ? 'Uploading video…' : 'Submitting…') : 'Submit'}
                </button>
              </div>
            </form>
      </Modal>
    </>
  )
}

export default Opentimelinemodal
